import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MIN_COL_WIDTH = 72;
const SELECT_COL_KEY = '_select';

const storageKeyFor = (tableClassName, tableBaseClass) =>
  `smartmirror.adminTableCols.${tableClassName || tableBaseClass || 'admin-table'}`;

const readStoredWidths = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, width]) => Number.isFinite(width) && width >= MIN_COL_WIDTH)
    );
  } catch {
    return {};
  }
};

/**
 * Renders admin list items in grid or table view with row/card selection.
 * Table columns can be resized (drag the header edge) and cell text wraps to the column width.
 */
const AdminSelectableList = ({
  items,
  getItemId,
  viewMode,
  isSelected,
  onToggleItem,
  allSelected,
  someSelected,
  onSelectAll,
  onClearSelection,
  columns = [],
  renderGridCard,
  emptyState,
  scrollRef,
  tableClassName = '',
  tableBaseClass = 'admin-table',
  tableWrapperClassName = 'admin-table-container admin-scroll-list',
  listClassName = '',
  footer = null,
  selectable = true
}) => {
  const tableClasses = [tableBaseClass, tableClassName].filter(Boolean).join(' ');
  const storageKey = useMemo(
    () => storageKeyFor(tableClassName, tableBaseClass),
    [tableClassName, tableBaseClass]
  );
  const tableRef = useRef(null);
  const dragRef = useRef(null);
  const [colWidths, setColWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    setColWidths(readStoredWidths(storageKey));
  }, [storageKey]);

  useEffect(
    () => () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    []
  );

  const persistWidths = useCallback(
    (next) => {
      try {
        if (!next || Object.keys(next).length === 0) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, JSON.stringify(next));
        }
      } catch {
        /* ignore quota / private mode */
      }
    },
    [storageKey]
  );

  const colWidthStyle = useCallback(
    (key) => {
      const width = colWidths[key];
      if (!width) return undefined;
      return { width, minWidth: width, maxWidth: width };
    },
    [colWidths]
  );

  const startResize = useCallback((colKey, event) => {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const table = tableRef.current || handle.closest('table');
    if (!table) return;
    const headers = [...table.querySelectorAll('thead th[data-col-key]')];
    const snapshot = {};
    headers.forEach((header) => {
      const key = header.getAttribute('data-col-key');
      if (key) snapshot[key] = Math.round(header.getBoundingClientRect().width);
    });
    dragRef.current = {
      colKey,
      startX: event.clientX,
      startWidth: snapshot[colKey] || MIN_COL_WIDTH,
      snapshot
    };
    setIsResizing(true);
    setColWidths(snapshot);

    const onMove = (moveEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const nextWidth = Math.max(
        MIN_COL_WIDTH,
        Math.round(drag.startWidth + (moveEvent.clientX - drag.startX))
      );
      setColWidths({ ...drag.snapshot, [drag.colKey]: nextWidth });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setIsResizing(false);
      setColWidths((current) => {
        persistWidths(current);
        return current;
      });
      dragRef.current = null;
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [persistWidths]);

  const resetColumnWidth = useCallback(
    (colKey, event) => {
      event.preventDefault();
      event.stopPropagation();
      setColWidths((prev) => {
        const next = { ...prev };
        delete next[colKey];
        persistWidths(next);
        return next;
      });
    },
    [persistWidths]
  );

  const hasCustomWidths = Object.keys(colWidths).length > 0;
  const tableMinWidth = hasCustomWidths
    ? Object.values(colWidths).reduce((sum, width) => sum + width, 0)
    : undefined;

  if (!items?.length) {
    return (
      <div
        className={viewMode === 'table' ? tableWrapperClassName : `admin-list admin-scroll-list ${listClassName}`.trim()}
        ref={scrollRef}
      >
        {emptyState}
        {footer}
      </div>
    );
  }

  if (viewMode === 'table') {
    return (
      <div className={tableWrapperClassName} ref={scrollRef}>
        <table
          ref={tableRef}
          className={`${tableClasses}${isResizing ? ' is-resizing' : ''}${hasCustomWidths ? ' has-col-widths' : ''}`}
          style={tableMinWidth ? { minWidth: tableMinWidth } : undefined}
        >
          {hasCustomWidths && (
            <colgroup>
              {selectable && <col style={colWidthStyle(SELECT_COL_KEY)} />}
              {columns.map((col) => (
                <col key={col.key} style={colWidthStyle(col.key)} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              {selectable && (
                <th className="col-select" data-col-key={SELECT_COL_KEY} style={colWidthStyle(SELECT_COL_KEY)}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={() => (allSelected ? onClearSelection() : onSelectAll())}
                    aria-label="Select all rows"
                  />
                  <button
                    type="button"
                    className="admin-table-col-resizer"
                    aria-label="Resize select column"
                    onMouseDown={(e) => startResize(SELECT_COL_KEY, e)}
                    onDoubleClick={(e) => resetColumnWidth(SELECT_COL_KEY, e)}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th key={col.key} data-col-key={col.key} style={colWidthStyle(col.key)} title={col.label}>
                  <span className="admin-table-col-label">{col.label}</span>
                  <button
                    type="button"
                    className="admin-table-col-resizer"
                    aria-label={`Resize ${col.label} column`}
                    onMouseDown={(e) => startResize(col.key, e)}
                    onDoubleClick={(e) => resetColumnWidth(col.key, e)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const id = getItemId(item);
              const selected = selectable && isSelected(id);
              return (
                <tr key={id} className={selected ? 'selected' : ''}>
                  {selectable && (
                    <td className="col-select" style={colWidthStyle(SELECT_COL_KEY)}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onToggleItem(id)}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} style={colWidthStyle(col.key)}>
                      {col.render ? col.render(item) : (item[col.key] ?? '-')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {footer}
      </div>
    );
  }

  return (
    <div className={`admin-list admin-scroll-list ${listClassName}`.trim()} ref={scrollRef}>
      {items.map((item) => {
        const id = getItemId(item);
        if (!selectable) {
          return <React.Fragment key={id}>{renderGridCard(item)}</React.Fragment>;
        }
        const selected = isSelected(id);
        return (
          <div key={id} className={`admin-selectable-item${selected ? ' is-selected' : ''}`}>
            <label className="admin-selectable-check">
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleItem(id)}
                aria-label="Select item"
              />
            </label>
            <div className="admin-selectable-content">{renderGridCard(item)}</div>
          </div>
        );
      })}
      {footer}
    </div>
  );
};

export default AdminSelectableList;
